import React, {useMemo} from 'react';
import {classes, style} from './doppe-layout5-profile-view-template.st.css';
import {BymoTemplateMetadataInferBymoPageProps} from '../../../doppe-sdk';
import {flexColumn, flexJustifyContentCenter} from '@wix/devzai-utils-react';
import {arrayFirst, arrayGenerate} from '@wix/devzai-utils-common';
import {FlexLayout} from '@wix/devzai-common-client';
import {doppeHideableValueIsVisible} from '../../../client-server-common/types/doppe-hideable-value';
import {DoppeHideableValueRenderer} from '../../doppe-hideable-value-renderer/doppe-hideable-value-renderer';
import {DoppePageProfileNameView} from '../../doppe-page-profile-name-view/doppe-page-profile-name-view';
import {bymoTemplateDesktopLayoutIsSplitLayout, DoppeProfileNameAutoSource} from '../../../client-server-common';
import {
    BymoTemplateSpeedDialTemplateMetadata
} from '../../bymo-templates/bymo-template-speed-dial/bymo-template-speed-dial.metadata';
import {DoppeLayoutRoot} from '../dopp-layouts-components/doppe-layout-root/doppe-layout-root';
import {DoppLayoutContent} from '../dopp-layouts-components/dopp-layout-content/dopp-layout-content';
import {
    DoppLayoutBackgroundMedia
} from '../dopp-layouts-components/dopp-layout-background-media/dopp-layout-background-media';
import {DoppePageProfilePicture} from '../../doppe-page-profile-picture/doppe-page-profile-picture';
import {DoppeLayoutPageContent} from '../dopp-layouts-components/doppe-layout-page-content/doppe-layout-page-content';
import {DoppeLayoutSocialLinks} from '../dopp-layouts-components/doppe-layout-social-links/doppe-layout-social-links';
import {DoppeLayoutBioText} from '../dopp-layouts-components/doppe-layout-bio-text/doppe-layout-bio-text';
import {DoppeLayout5HeroPhoto} from "./doppe-layout5-hero-photo";
import {
    useDoppeViewerPageDesktopLayout
} from "../../doppe-viewer-bymo-page-props-react/doppe-viewer-bymo-page-props-react";

export const DoppeLayout5ProfileViewTemplate = React.memo(function DoppeLayout5ProfileView(
    props: DoppeLayout5ProfileViewTemplate.Props
) {
    const {
        profilePictureLinkInBio,
        profilePictureMaskingShape,
        bioText,
        bioTextMaxLines,
        socialLinks,
        heroPhotos,
    } = props;

    const hasProfileImage = useMemo(() => {
        return doppeHideableValueIsVisible(profilePictureLinkInBio);
    }, [profilePictureLinkInBio]);

    const isSplitLayout = bymoTemplateDesktopLayoutIsSplitLayout(useDoppeViewerPageDesktopLayout());

    return (
        <DoppeLayoutRoot
            className={style(classes.root, {
                withProfileImage: hasProfileImage,
                hasHeroPhoto: true,
            })}
            hideHoppBanner={props.removeHoppBanner}
            layoutBackground={(
                <DoppLayoutBackgroundMedia
                    className={style(classes.pageWithCoverBackgroundView, {})}
                />
            )}
            sideContent={(
                <div
                    className={style(classes.sideContent, flexColumn(), flexJustifyContentCenter())}
                >
                    <div
                        className={style(classes.sideProfileImagesRow, {}, FlexLayout.row.alignToCenter)}
                    >
                        {arrayGenerate(3, index => {
                            return (
                                <DoppeLayout5HeroPhoto
                                    key={index}
                                    className={style(classes.sideContentHeroPhoto, {})}
                                    mediaResource={heroPhotos === null ? null : (arrayFirst(heroPhotos) ?? null)}
                                    widthSpec={{
                                        width: 422
                                    }}
                                />
                            )
                        })}
                    </div>
                </div>
            )}
        >
            <DoppLayoutContent
                className={style(classes.content)}
            >
                <header
                    className={style(classes.profileView, FlexLayout.column.alignToCenter)}
                >
                    {
                        hasProfileImage ?
                            <DoppeHideableValueRenderer
                                hideableValue={profilePictureLinkInBio}
                                renderFunction={profilePicture => (
                                    <DoppePageProfilePicture
                                        className={style(classes.profileImage, {})}
                                        profilePicture={profilePicture}
                                        maskingShape={profilePictureMaskingShape}
                                        size={104}
                                        sizeOnWideLayout={isSplitLayout ? 140 : 104}
                                    />
                                )}
                            />
                            :
                            null
                    }
                    <DoppePageProfileNameView
                        className={style(classes.profileName, {}, FlexLayout.verticallyJustifyToEnd)}
                        defaultProfileNameAutoSource={DoppeProfileNameAutoSource.AccountName}
                    />
                    <DoppeLayoutBioText
                        className={style(classes.bioText, {})}
                        bioText={bioText}
                        bioTextMaxLines={bioTextMaxLines}
                        lineHeight={'18px'}
                    />
                    <DoppeLayoutSocialLinks
                        className={style(classes.socialLinks, {})}
                        socialLinks={socialLinks}
                    />
                    <div
                        className={style(classes.profileImagesRow, {}, FlexLayout.row.alignToCenter)}
                    >
                        {arrayGenerate(3, index => {
                            return (
                                <DoppeLayout5HeroPhoto
                                    key={index}
                                    className={style(classes.heroPhoto, {})}
                                    mediaResource={heroPhotos === null ? null : (arrayFirst(heroPhotos) ?? null)}
                                    widthSpec={{contentRelativeWidth: 0.66}}
                                />
                            )
                        })}
                    </div>
                </header>
                <DoppeLayoutPageContent
                    pageProps={props}
                    searchHeaderClassName={style(classes.supportText)}
                    showSeparatorAboveSearchSection={true}
                />
            </DoppLayoutContent>
        </DoppeLayoutRoot>
    )
});

export namespace DoppeLayout5ProfileViewTemplate {

    export type Props = BymoTemplateMetadataInferBymoPageProps<typeof BymoTemplateSpeedDialTemplateMetadata>;
}

